/* Live operating widgets: Incidents, Prod deploys. My Work lives in myWorkWidget.tsx, Uptime Monitors in monitorsWidget.tsx. */
import type { inferRouterOutputs } from "@trpc/server"
import { useCallback, useState } from "react"
import toast from "react-hot-toast"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { useAuth } from "../../../lib/auth-context"
import { trpc } from "../../../lib/trpc"
import { ago, worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IRowAction, IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { KeyValues } from "./WidgetParts"
import { WIDGET_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type RouterOutputs = inferRouterOutputs<AppRouter>

const DEPLOYS_LIMIT = 20
const INCIDENTS_LIMIT = 50
const QUIET_AFTER_DAYS = 2
const DAY_MS = 864e5

// ─── Incidents ───────────────────────────────────────────────────────
type TIncident = RouterOutputs["incidents"]["list"][number]

const RESOLVE_REASONS = ["Fixed", "Recovered on its own", "False alarm"]
const MAX_REASON_LENGTH = 500

/** Resolve archives the Slack channel, so it asks for a reason: presets or free text. Opened by the `e` action. */
function IncidentResolveForm({ incident }: { incident: TIncident }) {
  const utils = trpc.useUtils()
  const { user } = useAuth()
  const [reason, setReason] = useState("")
  const resolve = trpc.incidents.resolve.useMutation(incidentMutationOptions(utils, "Incident resolved"))
  const submit = (text: string) => {
    const trimmed = text.trim()
    if (trimmed) resolve.mutate({ id: incident.id, actor: user?.email ?? "command-center", reason: trimmed })
  }
  return (
    <form
      className="hd-actions"
      onSubmit={(e) => {
        e.preventDefault()
        submit(reason)
      }}
    >
      <span className="hd-muted">Resolve:</span>
      {RESOLVE_REASONS.map((preset) => (
        <button key={preset} type="button" disabled={resolve.isLoading} onClick={() => submit(preset)}>
          {preset}
        </button>
      ))}
      <input
        className="hd-reason"
        aria-label="Resolution reason"
        placeholder="Or type a reason, Enter to resolve"
        value={reason}
        maxLength={MAX_REASON_LENGTH}
        onChange={(e) => setReason(e.target.value)}
        disabled={resolve.isLoading}
      />
    </form>
  )
}

const incidentMutationOptions = (utils: ReturnType<typeof trpc.useUtils>, label: string) => ({
  onSuccess: () => {
    toast.success(label)
    void utils.incidents.list.invalidate()
    void utils.myWork.getQueue.invalidate()
  },
  onError: (error: { message: string }) => toast.error(error.message || `${label} failed`),
})

type TIncidentActionsFor = (incident: TIncident) => IRowAction[]

/** a ack (until acked) · e resolve… (opens the reason form) */
const useIncidentActions = (): TIncidentActionsFor => {
  const utils = trpc.useUtils()
  const { user } = useAuth()
  const ack = trpc.incidents.ack.useMutation(incidentMutationOptions(utils, "Incident acked"))
  const actor = user?.email ?? "command-center"
  return useCallback(
    (incident: TIncident) => [
      ...(incident.ackedAt
        ? []
        : [
            {
              key: "a" as const,
              label: "Ack",
              disabled: ack.isLoading,
              run: () => ack.mutate({ id: incident.id, actor }),
            },
          ]),
      { key: "e" as const, label: "Resolve…", expand: true as const },
    ],
    [ack.isLoading, ack.mutate, actor],
  )
}

const incidentVerdict = (incident: TIncident): EVerdict => {
  if (incident.status === "mitigated") return EVerdict.WATCH
  return incident.severity === "sev0" || incident.severity === "sev1" || !incident.ackedAt
    ? EVerdict.ACT
    : EVerdict.WATCH
}

const renderIncidents = (list: TIncident[], actionsFor: TIncidentActionsFor): IWidgetResult => {
  const now = Date.now()
  const open = list.filter((incident) => !incident.resolvedAt)
  const rows: IWidgetRow[] = open.map((incident) => {
    const verdict = incidentVerdict(incident)
    return {
      key: incident.id,
      verdict,
      pill: { text: incident.severity ?? incident.status, verdict },
      title: incident.title,
      sub: [incident.project, incident.source, incident.status, incident.ackedAt ? "acked" : "not acked"].join(" · "),
      meta: ago(incident.openedAt, now),
      details: (
        <>
          {incident.summary && <p>{incident.summary}</p>}
          <KeyValues
            pairs={[
              ["Opened", new Date(incident.openedAt).toLocaleString()],
              ["Failures", String(incident.consecutiveFailureCount)],
              ["Slack", incident.slackChannelName ? `#${incident.slackChannelName}` : null],
            ]}
          />
          <IncidentResolveForm incident={incident} />
        </>
      ),
      href: `/devtools/incidents/${incident.id}`,
      hrefLabel: "Open incident",
      company: incident.project,
      actions: actionsFor(incident),
    }
  })
  return {
    verdict: worstVerdict(rows.map((r) => r.verdict)),
    count: `${open.length} open`,
    rows,
    empty: "No open incidents.",
  }
}

export const incidentsWidget: IWidgetDefinition = {
  id: "incidents",
  title: "Incidents",
  description: "Open incidents across every project",
  columns: { 2: 2, 3: 2, 4: 3, 5: 3, 6: 4 },
  link: { href: "/devtools/incidents", label: "Open Incidents" },
  useWidget: () => {
    const actionsFor = useIncidentActions()
    const render = useCallback((list: TIncident[]) => renderIncidents(list, actionsFor), [actionsFor])
    return useQueryWidget(
      trpc.incidents.list.useQuery({ status: "any", limit: INCIDENTS_LIMIT }, WIDGET_QUERY_OPTIONS),
      render,
    )
  },
}

// ─── Prod deploys ────────────────────────────────────────────────────
type TDeploy = RouterOutputs["deployFeed"]["list"][number]

/** Same name + members as the API's enum, so TS accepts it (as in DeployFeedScreen) */
enum EDeployFeedScope {
  RELEASES = "releases",
  PRODUCTION = "production",
  ALL = "all",
}

const deployVerdict = (deploy: TDeploy): EVerdict => {
  if (deploy.deployStatus === "failed" || deploy.healthStatus === "failed" || deploy.healthStatus === "degraded")
    return EVerdict.ACT
  if (deploy.deployStatus === "running" || deploy.healthStatus === "analyzing") return EVerdict.WATCH
  if (deploy.healthStatus === "healthy") return EVerdict.CLEAR
  return EVerdict.UNKNOWN
}

const renderDeploys = (list: TDeploy[]): IWidgetResult => {
  const now = Date.now()
  const rows: IWidgetRow[] = list.map((deploy) => {
    const verdict = deployVerdict(deploy)
    const old = now - new Date(deploy.startedAt).getTime() > QUIET_AFTER_DAYS * DAY_MS
    return {
      key: deploy.id,
      verdict,
      pill: { text: deploy.deployStatus === "succeeded" ? deploy.healthStatus : deploy.deployStatus, verdict },
      title: deploy.projectName,
      sub: [deploy.service, deploy.sha?.slice(0, 7), deploy.deployedBy].filter(Boolean).join(" · "),
      meta: ago(deploy.startedAt, now),
      quiet: verdict === EVerdict.CLEAR || (old && verdict !== EVerdict.ACT),
      details: (
        <>
          <KeyValues
            pairs={[
              ["Branch", deploy.branch],
              ["Environment", deploy.environment],
              ["Signals", String(deploy.signalCount)],
              ["Completed", deploy.completedAt ? ago(deploy.completedAt, now) : "not yet"],
            ]}
          />
        </>
      ),
      href: deploy.deployUrl ?? undefined,
      hrefLabel: "Open deploy",
      company: deploy.projectName,
    }
  })
  return {
    verdict: worstVerdict(rows.map((r) => r.verdict)),
    count: `${rows.filter((r) => !r.quiet).length} to look at`,
    rows,
    ordered: false,
    quietLabel: `${rows.filter((r) => r.quiet).length} healthy or older`,
    empty: "No production deploys.",
  }
}

export const deploysWidget: IWidgetDefinition = {
  id: "deploys",
  title: "Prod deploys",
  description: "Latest production deploys and their health",
  columns: { 2: 2, 3: 3, 4: 4, 5: 4, 6: 5 },
  link: { href: "/devtools/deploy-feed", label: "Open Deploy feed" },
  useWidget: () =>
    useQueryWidget(
      trpc.deployFeed.list.useQuery({ scope: EDeployFeedScope.PRODUCTION, limit: DEPLOYS_LIMIT }, WIDGET_QUERY_OPTIONS),
      renderDeploys,
    ),
}
